/**
 * RESTRUCTURE impulses:
 *
 * Impulses are saved in a hierarchical order now, ordered by simulationId and roundIndex by default.
 * items: {
 *   [simulationId]: `{
 *     [roundIndex]: {
 *       element: {
 *         [elementId]: impulse
 *       },
 *       resource: {
 *         [resourceId]: impulse
 *       }
 *     }
 *   }`
 * }
 */

import { groupBy, keyBy, mapValues } from "lodash-es";

export default {
  name: "restructure impulses",
  applyToVersions: "<1.32.0",
  type: "before",
  migrate(data) {
    if (!data.impulses) return data;
    const impulses =
      typeof data.impulses?.items === "object"
        ? data.impulses.items
        : JSON.parse(data.impulses?.items || "{}");

    // group impulses by simulationId first
    const impulsesByGameId = groupBy(impulses, "gameId");

    // group the simulationId-impulse-groups by roundCreated next and convert them to JSON strings
    for (const simulationId in impulsesByGameId) {
      const impulseGroup = impulsesByGameId[simulationId];
      const impulsesByRound = groupBy(impulseGroup, "roundCreated");

      // restructure sourceValues
      reassignSourceValues(
        impulseGroup,
        data.relations.items,
        data.elementResources.items
      );

      for (const roundIndex in impulsesByRound) {
        const roundImpulses = impulsesByRound[roundIndex];
        // group the impulses by element and resources
        const elementImpulses = keyBy(
          roundImpulses.filter((impulse) => impulse.affectedType === "element"),
          "affectedId"
        );
        const resourceImpulses = keyBy(
          roundImpulses.filter(
            (impulse) => impulse.affectedType === "resource"
          ),
          "affectedId"
        );

        // remove obsolete properties
        Object.values(elementImpulses).forEach(cleanImpulse);
        Object.values(resourceImpulses).forEach(cleanImpulse);

        // overwrite the resources with the newly ordered ones:
        impulsesByRound[roundIndex] = {
          element: elementImpulses,
          resource: resourceImpulses
        };
      }

      impulsesByGameId[simulationId] = JSON.stringify(impulsesByRound);
    }

    data.impulses.items = impulsesByGameId;

    // The impulses have to be loaded on simulation load now.
    // if there's a loaded simulation, add the impulses to the simulation
    if (data.simulation.runningGameId != null) {
      data.simulation.gameImpulses = JSON.parse(
        data.impulses.items[data.simulation.runningGameId] || "{}"
      );
    }

    return data;
  }
};

function cleanImpulse(impulse) {
  removeSrcTypeAndSrcId(impulse);
  removeAffectedTypeAndId(impulse);
  delete impulse.id;
  delete impulse.roundId;
  delete impulse.gameId;
  delete impulse.spawned;
  delete impulse.parentImpulseId;
  delete impulse.delay;
  delete impulse.roundApplied;
  delete impulse.roundCreated;
}

function removeSrcTypeAndSrcId(impulse) {
  delete impulse.srcType;
  delete impulse.srcId;
}

function removeAffectedTypeAndId(impulse) {
  delete impulse.affectedType;
  delete impulse.affectedId;
}

/**
 * The source value information changed: instead of referencing the parent impulse,
 * the source information contains the way the impulseSource came though
 * (elementResource or relation)
 * --> Translate the current information saved with impulse ids to the info with relation ids
 * @param {Impulse[]} gameImpulses
 * @param {Relation[]} relations
 * @param {ElementResource[]} elementResources
 */
function reassignSourceValues(gameImpulses, relations, elementResources) {
  const impulsesById = keyBy(gameImpulses, "id");
  const orderedRelations = mapValues(
    groupBy(relations, "startElementId"),
    (relations) => keyBy(relations, "endElementId")
  );
  const orderedElementResources = mapValues(
    groupBy(elementResources, "elementId"),
    (elResources) => keyBy(elResources, "resourceId")
  );

  for (const impulseId in impulsesById) {
    const impulse = impulsesById[impulseId];
    let sourceValueIndex = impulse.sourceValues.length;
    while (sourceValueIndex--) {
      const sourceValue = impulse.sourceValues[sourceValueIndex];
      if (sourceValue.type == "impulse") {
        const sourceImpulse = impulsesById[sourceValue.id];
        const elementId = sourceImpulse.affectedId;

        // save the relation as the new source, replacing the impulse itself.
        let item = null;
        if (impulse.affectedType == "resource") {
          const relations = orderedElementResources[elementId];
          if (relations) item = relations[impulse.affectedId];
        } else if (impulse.affectedType == "element") {
          const relations = orderedRelations[elementId];
          if (relations) item = relations[impulse.affectedId];
        }

        if (item != null) {
          sourceValue.type = item.type;
          sourceValue.id = item.id;
        } else {
          // the relation does not exist anymore:
          // remove the sourcevalue
          impulse.sourceValues.splice(sourceValueIndex, 1);
        }
      }
    }
  }
}

/**
 * @typedef {Object} Impulse
 * @property {Number} id
 * @property {Array} sourceValues
 */
